<template>
  <div class="page-wrap" :style="themeStyles">

    <!-- ── VIEW 1: UNITS LIST ─────────────────── -->
    <template v-if="viewMode === 'list'">
      <PremiumTable title="Unit Management" subtitle="Manage society residential houses and commercial shops"
        :show-edit="true" :show-delete="true" @edit="openEditModal" @delete="confirmDelete" :columns="columns"
        :items="units" :loading="loading" :total-items="totalRecords" :page.sync="page"
        :items-per-page.sync="itemsPerPage" :searchable="true" :search="search" @search="onSearch">
        <template #add-button>
          <div class="unit-management-header-actions">
            <button class="btn-premium-primary" :style="{ '--btn-color': brandColor }" @click="openAddModal"
              type="button">
              <v-icon size="16" color="white">mdi-plus</v-icon>
              <span>Add Unit</span>
            </button>
          </div>
        </template>

        <!-- Header Extra Slot -->
        <template #header-extra>

          <!-- Block filter — same as member list -->
          <FilterDropdown v-if="flats.length > 0" v-model="selected_flat" :options="flats" item-text="name"
            item-value="id" placeholder="Filter by Block" @change="refreshTable" />


          <button class="btn-premium-primary ml-2" :style="{ '--btn-color': brandColor }" @click="viewMode = 'generate'"
            type="button">
            <v-icon size="16" color="white">mdi-flash-outline</v-icon>
            <span>Bulk Generate</span>
          </button>
        </template>

        <!-- Custom Column: Unit Number / Type -->
        <template #col-house_number="{ item }">
          <div class="unit-cell">
            <v-icon size="18" :color="primaryColor || '#3B82F6'" class="mr-2">
              {{ String(item.unit_type) === '2' ? 'mdi-store-outline' : 'mdi-home-outline' }}
            </v-icon>
            <span class="unit-name-text">
              {{ item.block_name ? `${item.block_name}-${item.house_number}` : item.house_number }}
            </span>
            <span v-if="String(item.unit_type) === '2'" class="type-badge shop-badge ml-2">Shop</span>
            <span v-else class="type-badge house-badge ml-2">House</span>
          </div>
        </template>

        <!-- Custom Column: Status -->
        <template #col-occupancy_status="{ item }">
          <span class="status-badge" :class="getStatusClass(item)">
            {{ getStatusLabel(item) }}
          </span>
        </template>

        <!-- Custom Column: Member Name -->
        <template #col-member_name="{ item }">
          <div class="members-cell" v-if="item && item.approval_status === 'approved'">
            <div v-if="item.tenant_name" class="member-row">
              <span class="role-lbl tenant">Tenant</span>
              <span class="member-val">{{ item.tenant_name }}</span>
            </div>
            <div v-if="item.owner_name" class="member-row">
              <span class="role-lbl owner">Owner</span>
              <span class="member-val">{{ item.owner_name }}</span>
            </div>
            <div v-if="!item.tenant_name && !item.owner_name" class="no-members">
              —
            </div>
          </div>
          <span v-else-if="item">—</span>
        </template>
      </PremiumTable>
    </template>

    <!-- ── VIEW 2: BULK GENERATE PAGE ─────────── -->
    <template v-else-if="viewMode === 'generate'">
      <PremiumPage title="Bulk Generate Units" subtitle="Configure pattern to generate multiple units at once"
        :show-back="true" @back="viewMode = 'list'" :show-footer-buttons="true" submit-label="Generate"
        cancel-label="Cancel" :loading="genLoading" @submit="bulkGenerate" @cancel="viewMode = 'list'">
        <div class="bulk-page-content-wrapper">
          <div class="bulk-layout-grid">

            <!-- ── LEFT: Config Column ────────────────── -->
            <div class="bulk-config-col">

              <!-- Unit Type Selector —Mixed society: both types. Commercial-only: shop forced. Residential: hidden. -->
              <div v-if="societyIsMixed || isCommercialOnly" class="form-card mb-4">
                <label class="form-label">Unit Type</label>
                <div v-if="isCommercialOnly" class="type-selector">
                  <button type="button" class="type-btn active" disabled>
                    <v-icon small class="mr-1">mdi-store-outline</v-icon> Shop
                  </button>
                </div>
                <div v-else class="type-selector">
                  <button type="button" class="type-btn" :class="{ active: bulkUnitType === '1' }"
                    @click="bulkUnitType = '1'">
                    <v-icon small class="mr-1">mdi-home-outline</v-icon> House
                  </button>
                  <button type="button" class="type-btn" :class="{ active: bulkUnitType === '2' }"
                    @click="bulkUnitType = '2'">
                    <v-icon small class="mr-1">mdi-store-outline</v-icon> Shop
                  </button>
                </div>
              </div>

              <!-- Blocks selection -->
              <div v-show="societyType === 'multi' && !(bulkUnitType === '2' && shopActsAsSingleBuilding)"
                class="form-card mb-4">
                <div class="section-head d-flex align-center justify-space-between mb-3">
                  <span class="form-label mb-0">Select Blocks / Wings</span>
                  <span class="select-link-btn" @click="toggleAllBlocks">
                    {{ selectedBlocks.size === availableBlocks.length ? 'Deselect All' : 'Select All' }}
                  </span>
                </div>
                <div class="block-pills">
                  <div v-for="b in availableBlocks" :key="b.id" class="block-pill"
                    :class="{ selected: selectedBlocks.has(b.name) }" @click="toggleBlock(b.name)">
                    <div class="pill-dot"></div>
                    {{ b.name }}
                  </div>
                </div>
              </div>

              <!-- Pattern Type -->
              <div class="form-card">
                <div class="form-group mb-4">
                  <label class="form-label">Pattern Type</label>
                  <div class="pattern-tabs">
                    <button type="button" class="ptab" :class="{ active: activePattern === 'sequential' }"
                      @click="switchPattern('sequential')">
                      Sequential<br /><span class="ptab-sub">1,2,3…</span>
                    </button>
                    <button type="button" class="ptab" v-show="bulkUnitType === '2' || societyType !== 'multi'"
                      :class="{ active: activePattern === 'prefix' }" @click="switchPattern('prefix')">
                      Prefix + Number<br /><span class="ptab-sub">A1,A2…</span>
                    </button>
                    <button type="button" class="ptab" :class="{ active: activePattern === 'floor' }"
                      @click="switchPattern('floor')">
                      Floor + Unit<br /><span class="ptab-sub">101,102…</span>
                    </button>
                  </div>
                </div>

                <!-- Sequential Pattern Config -->
                <div v-if="activePattern === 'sequential'" class="pattern-fields">
                  <div class="row3">
                    <PremiumInput v-model.number="config.s_start" type="number" label="Start" min="1" />
                    <PremiumInput v-model.number="config.s_end" type="number" label="End" min="1" />
                    <PremiumInput v-model.number="config.s_step" type="number" label="Step" min="1" />
                  </div>
                </div>

                <!-- Prefix Pattern Config -->
                <div v-if="activePattern === 'prefix'" class="pattern-fields">
                  <PremiumInput v-model="config.p_prefix" label="Prefix" placeholder="e.g. A, B, S-"
                    icon="mdi-tag-outline" />
                  <div class="row2 mt-3">
                    <PremiumInput v-model.number="config.p_start" type="number" label="Start" min="1" />
                    <PremiumInput v-model.number="config.p_end" type="number" label="End" min="1" />
                  </div>
                  <div class="mt-3">
                    <PremiumSelect v-model="config.p_pad" label="Number Padding" :items="[
                      { text: 'None — 1, 2, 3', value: 0 },
                      { text: '2 digits — 01, 02', value: 2 },
                      { text: '3 digits — 001, 002', value: 3 },
                    ]" item-text="text" item-value="value" icon="mdi-numeric" />
                  </div>
                </div>

                <!-- Floor Pattern Config -->
                <div v-if="activePattern === 'floor'" class="pattern-fields">
                  <div class="row2 mb-3">
                    <PremiumInput v-model.number="config.f_startFloor" type="number" label="Starting Floor" min="0" />
                    <PremiumInput v-model.number="config.f_floors" type="number" label="No. of Floors" min="1" />
                  </div>
                  <PremiumInput v-model.number="config.f_units" type="number" label="Units per Floor" min="1" />
                  <div class="mt-3">
                    <PremiumSelect v-model="config.f_pad" label="Number Padding" :items="[
                      { text: '3 digits — 101, 102', value: 2 },
                      { text: '4 digits — 1001, 1002', value: 3 },
                    ]" item-text="text" item-value="value" icon="mdi-numeric" />
                  </div>
                </div>
              </div>
            </div>

            <!-- ── RIGHT: Live Preview Column ─────────── -->
            <div class="bulk-preview-col">
              <div class="preview-panel">
                <!-- Header -->
                <div class="preview-panel-header">
                  <div class="preview-panel-icon">
                    <v-icon size="16" color="white">{{ bulkUnitType === '2' ? 'mdi-store-outline' : 'mdi-home-outline'
                    }}</v-icon>
                  </div>
                  <div>
                    <div class="preview-panel-title">Live Preview</div>
                    <div class="preview-panel-sub">{{ bulkUnitType === '2' ? 'Shops' : 'Houses' }}</div>
                  </div>
                  <div class="preview-total-badge" v-if="previewTotal > 0">
                    {{ previewTotal }} units
                  </div>
                </div>

                <!-- Chip grid -->
                <div class="preview-chips-area">
                  <template v-if="previewChips.length > 0">
                    <span v-for="(chip, idx) in previewChips" :key="idx" class="preview-chip"
                      :class="bulkUnitType === '2' ? 'preview-chip--shop' : 'preview-chip--house'">
                      {{ chip }}
                    </span>
                  </template>
                  <div v-else class="preview-empty">
                    <v-icon size="32" color="#cbd5e1">mdi-eye-outline</v-icon>
                    <span>Configure settings to preview units</span>
                  </div>
                </div>

                <!-- Footer summary -->
                <div class="preview-panel-footer" v-if="previewTotal > 0">
                  <span class="preview-footer-stat">
                    <v-icon size="13">mdi-layers-outline</v-icon>
                    {{ activePattern === 'floor' ? config.f_floors + ' floor(s)' : activePattern }}
                  </span>
                  <span class="preview-footer-stat">
                    <v-icon size="13">mdi-pound</v-icon>
                    Total: <strong>{{ previewTotal }}</strong>
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </PremiumPage>
    </template>

    <!-- ── Add / Edit Unit Modal ────────────────── -->
    <PremiumModal v-model="unitModal" :title="editingUnit.id ? 'Edit Unit' : 'Add Unit'" :loading="saveLoading"
      submit-text="Save" max-width="480" @submit="saveUnit" @close="closeUnitModal">
      <div class="form-container">
        <!-- Unit Type (House / Shop) — only for mixed society, only on Add -->
        <div v-if="(societyIsMixed || isCommercialOnly) && !editingUnit.id" class="form-group">
          <label class="form-label">Unit Type</label>
          <div class="type-selector">
            <button v-if="!isCommercialOnly" type="button" class="type-btn"
              :class="{ active: editingUnit.unit_type === '1' }" @click="editingUnit.unit_type = '1'">
              <v-icon small class="mr-1">mdi-home-outline</v-icon> House
            </button>
            <button type="button" class="type-btn" :class="{ active: editingUnit.unit_type === '2' }"
              @click="editingUnit.unit_type = '2'">
              <v-icon small class="mr-1">mdi-store-outline</v-icon> Shop
            </button>
          </div>
        </div>


        <!-- Block / Wing Selection — only for multi-block societies, and only for houses (not shops when shopActsAsSingleBuilding) -->
        <PremiumSelect v-if="societyType === 'multi' && !(editingUnit.unit_type === '2' && shopActsAsSingleBuilding)"
          v-model="editingUnit.flat_id" label="Block / Wing" :items="availableBlocks" item-text="name" item-value="id"
          icon="mdi-office-building-outline" :disabled="!!editingUnit.id" />

        <!-- Unit Number -->
        <PremiumInput v-model="editingUnit.house_number"
          :label="editingUnit.unit_type === '2' ? 'Shop Number' : 'Unit Number'"
          :placeholder="editingUnit.unit_type === '2' ? 'e.g. S1, S-02' : 'e.g. 101, A-102'"
          :icon="editingUnit.unit_type === '2' ? 'mdi-store-outline' : 'mdi-home-outline'" name="house_number"
          required />

        <!-- Occupancy Status -->
        <PremiumSelect v-model="editingUnit.occupancy_status" label="Occupancy Status" :items="[
          { text: 'Vacant', value: 'vacant' },
          { text: 'Occupied', value: 'self_occupied' },
          { text: 'Closed', value: 'closed' }
        ]" item-text="text" item-value="value" icon="mdi-account-check-outline"
          :disabled="editingUnit.is_registered && editingUnit.approval_status === 'approved'" />
        <span class="field-hint">
          Note: Status is auto-set to "Occupied" when a member joins, and "Vacant" when deleted.
        </span>
        <template v-if="editingUnit.is_registered && editingUnit.approval_status === 'approved'">
          <div class="field-hint" style="font-weight:600; margin-top:3px;">Unit has an approved member — status is
            forced to Occupied.</div>
        </template>
      </div>
    </PremiumModal>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal v-model="deleteDialog" type="danger" title="Delete Unit"
      message="Are you sure you want to delete this unit?" confirm-text="Yes, Delete" cancel-text="Cancel"
      :loading="deleteLoading" @confirm="deleteUnit" />

  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import PremiumPage from "../../common/PremiumPage.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";

export default {
  name: "UnitManagement",
  components: { PremiumTable,FilterDropdown, PremiumModal, PremiumInput, PremiumSelect, PremiumPage, ConfirmModal },

  data() {
    return {
      search: "",
      units: [],
      loading: false,
      saveLoading: false,
      genLoading: false,
      deleteLoading: false,

      page: 1,
      itemsPerPage: 10,

      viewMode: "list", // "list" or "generate"

      societyType: "multi",
      societyIsMixed: false,         // true when society.type === 3 (mixed)
      isCommercialOnly: false,       // true when society.type === 2 (shops only)
      shopActsAsSingleBuilding: false, // true when type===3 AND has_multiple_building===1: shops are society-wide (no blocks)
      shopLimit: 0,                  // no_of_shop from society profile
      availableBlocks: [],
      selectedBlocks: new Set(),
      activePattern: "sequential",
      bulkUnitType: "1",    // '1' house, '2' shop

      config: {
        s_start: 1,
        s_end: 20,
        s_step: 1,
        p_prefix: "A",
        p_start: 1,
        p_end: 20,
        p_pad: 0,
        f_startFloor: 1,
        f_floors: 5,
        f_units: 4,
        f_pad: 2,
      },
      previewChips: [],
      previewTotal: 0,
      previewHidden: 0,
      previewText: "—",

      // Shop generation (mixed society)
      shopConfig: { start: 1, end: 10, step: 1 },
      shopPreviewText: "—",

      unitModal: false,
      deleteDialog: false,
      deleteId: null,

      editingUnit: {
        id: null,
        flat_id: null,
        house_number: "",
        unit_type: "1",
        occupancy_status: "vacant",
      },
      totalRecords: 0,
      selected_flat: "",
      flats: [],
      sortBy: "house_number",
      sortDesc: false,
      sortOptions: [
        { label: "Unit", sortBy: "house_number" },
        { label: "Status", sortBy: "occupancy_status" },
      ],
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),

    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },

    themeStyles() {
      const primary = this.primaryColor || "#0d9488";
      const button = this.buttonColor || this.primaryColor || "#0f766e";

      return {
        "--teal": primary,
        "--teal-dark": button,
        "--teal-light": "#ccfbf1",
        "--teal-muted": this.isDark ? "rgba(13, 148, 136, 0.1)" : "#e6f7f5",

        // Shop badge (use primary color)
        "--badge-shop-bg": this.isDark ? "rgba(239, 68, 68, 0.15)" : "#fee2e2",
        "--badge-shop-color": "#ef4444",

        // House badge (blue)
        "--badge-house-bg": this.isDark ? "rgba(3, 105, 161, 0.15)" : "#e0f2fe",
        "--badge-house-color": "#0369a1",

        // Status badges
        "--status-vacant-bg": this.isDark ? "rgba(100, 116, 139, 0.1)" : "#f1f5f9",
        "--status-vacant-color": this.isDark ? "#cbd5e1" : "#475569",
        "--status-occupied-bg": this.isDark ? "rgba(22, 101, 52, 0.15)" : "#dcfce7",
        "--status-occupied-color": this.isDark ? "#86efac" : "#166534",
        "--status-closed-bg": this.isDark ? "rgba(153, 27, 27, 0.15)" : "#fee2e2",
        "--status-closed-color": this.isDark ? "#fca5a5" : "#991b1b",

        // Role labels
        "--role-tenant-bg": this.isDark ? "rgba(217, 119, 6, 0.15)" : "#fef3c7",
        "--role-tenant-color": this.isDark ? "#fcd34d" : "#d97706",
        "--role-owner-bg": this.isDark ? "rgba(79, 70, 229, 0.15)" : "#e0e7ff",
        "--role-owner-color": this.isDark ? "#a5b4fc" : "#4f46e5",

        // Unit icon
        "--icon-unit-color": "#3B82F6",
      };
    },

    isShopSingleBuildingMode() {
      return this.bulkUnitType === '2' && this.shopActsAsSingleBuilding && this.societyType === 'multi';
    },

    columns() {
      const cols = [
        { key: "house_number", label: "Unit / Type" },
      ];
      // if (this.societyType === "multi") {
      //   cols.push({ key: "block_name", label: "Block" });
      // }
      cols.push({ key: "occupancy_status", label: "Status" });
      cols.push({ key: "member_name", label: "Members / Occupants" });
      return cols;
    },

  },

  watch: {
    societyType() { this.updatePreview(); },
    activePattern() { this.updatePreview(); },
    "config.s_start"() { this.updatePreview(); },
    "config.s_end"() { this.updatePreview(); },
    "config.s_step"() { this.updatePreview(); },
    "config.p_prefix"() { this.updatePreview(); },
    "config.p_start"() { this.updatePreview(); },
    "config.p_end"() { this.updatePreview(); },
    "config.p_pad"() { this.updatePreview(); },
    "config.f_startFloor"() { this.updatePreview(); },
    "config.f_floors"() { this.updatePreview(); },
    "config.f_units"() { this.updatePreview(); },
    "config.f_pad"() { this.updatePreview(); },
    "shopConfig.start"() { this.updateShopPreview(); },
    "shopConfig.end"() { this.updateShopPreview(); },
    "shopConfig.step"() { this.updateShopPreview(); },
    bulkUnitType() {
      this.updatePreview();
      this.updateShopPreview();
    },
    shopActsAsSingleBuilding() {
      this.updatePreview();
    },
    page() { this.getUnits(); },
    itemsPerPage() { this.getUnits(); },
    // search() {
    //   this.page = 1;
    // },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    _applySocietySettings(society) {
      const hasMultiple = society.has_multiple_building === 1
        || society.has_multiple_building === "1"
        || society.has_multiple_building === true;

      this.societyType = hasMultiple ? "multi" : "single";

      const sType = Number(society.type);
      if (sType === 2) {
        // Commercial: shops only
        this.societyIsMixed = false;
        this.isCommercialOnly = true;
        this.bulkUnitType = "2";
        this.shopLimit = Number(society.no_of_shop) || 0;
        // Commercial multi-building: shops still use blocks like houses do.
        this.shopActsAsSingleBuilding = false;
      } else if (sType === 3) {
        // Mixed: houses + shops
        this.societyIsMixed = true;
        this.isCommercialOnly = false;
        this.shopLimit = Number(society.no_of_shop) || 0;
        // ONLY when type===3 AND has_multiple_building===1:
        // shops act as a single building (society-wide, no block assignment).
        // Houses still use blocks normally.
        this.shopActsAsSingleBuilding = hasMultiple;
        this.updatePreview();
      } else {
        // Residential: houses only
        this.societyIsMixed = false;
        this.isCommercialOnly = false;
        this.shopActsAsSingleBuilding = false;
        this.bulkUnitType = "1";
      }
    },
    refreshTable() {
      this.page = 1;
      this.getUnits();
    },

    applySort(sortBy) {
      this.sortBy = sortBy;
      this.page = 1;
      this.getUnits();
    },

    toggleSortDir() {
      this.sortDesc = !this.sortDesc;
      this.page = 1;
      this.getUnits();
    },

    getFlats() {
      this.$axios.get("societyadmin/flats").then((response) => {
        if (response.data.status) {
          let flats = [...response.data.flats];
          if (this.societyIsMixed) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        }
      });
    },
    onSearch(val) {
      this.search = val;
      this.page = 1;
    },

    getStatusLabel(item) {
      const s = String(item.occupancy_status || "vacant").toLowerCase();

      if (s === "closed" || s === "close") return "Closed";
      if (s === "self_occupied" || s === "rented") return "Occupied";
      return "Vacant";
    },

    getStatusClass(item) {
      const s = String(item.occupancy_status || "vacant").toLowerCase();
      if (s === "closed" || s === "close") return "s-closed";
      if (s === "rented" || s === "self_occupied") return "s-occupied";
      return "s-vacant";
    },

    getBlocks() {
      this.$axios.get("societyadmin/flats").then((response) => {
        if (response.data.status) {
          this.availableBlocks = response.data.flats;
          this.availableBlocks.forEach((b) => this.selectedBlocks.add(b.name));
          this.selectedBlocks = new Set(this.selectedBlocks);
          this.updatePreview();
        }
      });
    },

    getUnits() {
      this.loading = true;
      let flat_id = this.selected_flat;
      let unit_type = "";

      if (flat_id === "all_shops") {
        flat_id = "";
        unit_type = 2;
      }

      this.$axios
        .get("societyadmin/units", {
          params: {
            flat_id: flat_id || "",
            unit_type: unit_type,
            page: this.page,
            itemsPerPage: this.itemsPerPage,
            sortBy: this.sortBy,
            sortDesc: this.sortDesc,
            search: this.search,
          },
        })
        .then((response) => {
          this.loading = false;
          if (response.data.status) {
            // ← NEW: response.data.data is now { page, total, lastPage, data: [] }
            this.units = response.data.data.data;
            this.totalRecords = response.data.data.total;
          }
        })
        .catch(() => { this.loading = false; });
    },

    openEditModal(unit) {
      // Normalize status to one of our 3 canonical values: vacant / occupied / closed
      let status = String(unit.occupancy_status || "vacant").toLowerCase();

      // If a unit has a registered user whose approval is 'approved', treat it as occupied
      if (unit.is_registered && unit.approval_status === 'approved') {
        status = 'self_occupied';
      } else
        if (status === "self_occupied" || status === "rented") {
          status = "self_occupied";
        } else if (status === "close") {
          status = "closed";
        } else if (status !== "vacant" && status !== "self_occupied" && status !== "closed") {
          // Any unknown legacy value → vacant
          status = "vacant";
        }
      this.editingUnit = {
        id: unit.id,
        flat_id: unit.flat_id,
        house_number: unit.house_number,
        unit_type: String(unit.unit_type || "1"),
        occupancy_status: status,
        // preserve registration info so modal can enforce immutability when approved
        is_registered: !!unit.is_registered,
        approval_status: unit.approval_status || null,
      };
      this.unitModal = true;
    },

    closeUnitModal() {
      this.unitModal = false;
    },

    saveUnit() {
      this.saveLoading = true;
      // Ensure occupancy_status cannot be overridden for registered+approved units
      if (this.editingUnit.is_registered && this.editingUnit.approval_status === 'approved') {
        this.editingUnit.occupancy_status = 'self_occupied';
      }

      const apiCall = this.editingUnit.id
        ? this.$axios.put(`societyadmin/units/${this.editingUnit.id}`, this.editingUnit)
        : this.$axios.post("societyadmin/units", this.editingUnit);

      apiCall
        .then((response) => {
          this.saveLoading = false;
          if (response.data.status) {
            this.showSuccess(response.data.message);
            this.closeUnitModal();
            this.getUnits();
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(() => {
          this.saveLoading = false;
        });
    },

    confirmDelete(unit) {
      this.deleteId = unit.id;
      this.deleteDialog = true;
    },

    deleteUnit() {
      this.deleteLoading = true;
      this.$axios
        .delete(`societyadmin/units/${this.deleteId}`)
        .then((response) => {
          this.deleteLoading = false;
          if (response.data.status) {
            this.showSuccess(response.data.message);
            this.deleteDialog = false;
            this.getUnits();
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    toggleBlock(name) {
      if (this.selectedBlocks.has(name)) {
        this.selectedBlocks.delete(name);
      } else {
        this.selectedBlocks.add(name);
      }
      this.selectedBlocks = new Set(this.selectedBlocks);
      this.updatePreview();
    },

    toggleAllBlocks() {
      if (this.selectedBlocks.size === this.availableBlocks.length) {
        this.selectedBlocks.clear();
      } else {
        this.availableBlocks.forEach((b) => this.selectedBlocks.add(b.name));
      }
      this.selectedBlocks = new Set(this.selectedBlocks);
      this.updatePreview();
    },

    switchPattern(name) {
      this.activePattern = name;
      this.updatePreview();
    },

    calculateTotalToBeGenerated() {
      const isShopsAsSingleBuilding = this.isShopSingleBuildingMode;
      const blockCount = isShopsAsSingleBuilding
        ? 1
        : (this.societyType === 'multi' ? Math.max(1, this.selectedBlocks.size) : 1);

      let unitsPerBlock = 0;
      if (this.activePattern === 'sequential') {
        const start = parseInt(this.config.s_start) || 1;
        const end = parseInt(this.config.s_end) || 20;
        const step = Math.max(1, parseInt(this.config.s_step) || 1);
        for (let i = start; i <= end; i += step) {
          unitsPerBlock++;
        }
      } else if (this.activePattern === 'prefix') {
        const start = parseInt(this.config.p_start) || 1;
        const end = parseInt(this.config.p_end) || 20;
        unitsPerBlock = Math.max(0, end - start + 1);
      } else if (this.activePattern === 'floor') {
        const floors = parseInt(this.config.f_floors) || 5;
        const unitsPerFloor = parseInt(this.config.f_units) || 4;
        unitsPerBlock = floors * unitsPerFloor;
      }
      return blockCount * unitsPerBlock;
    },

    updatePreview() {
      const isShopsAsSingleBuilding = this.isShopSingleBuildingMode;
      let blockArray;

      if (this.bulkUnitType === '1') {
        blockArray = this.societyType === "multi" ? [...this.selectedBlocks] : [""];
      } else if (isShopsAsSingleBuilding) {
        blockArray = [""];
      } else {
        blockArray = this.societyType === "multi" ? [...this.selectedBlocks] : [""];
      }

      if (this.bulkUnitType === '1' && blockArray.length === 0) {
        this.previewChips = [];
        this.previewTotal = 0;
        this.previewHidden = 0;
        this.previewText = "No blocks selected";
        return;
      }

      const allNums = [];

      for (const block of blockArray) {
        const prefixStr = block ? `${block}-` : "";

        if (this.activePattern === "sequential") {
          const start = parseInt(this.config.s_start) || 1;
          const end = parseInt(this.config.s_end) || 20;
          const step = Math.max(1, parseInt(this.config.s_step) || 1);
          for (let i = start; i <= end; i += step) {
            allNums.push(prefixStr + i);
          }
        } else if (this.activePattern === "prefix") {
          const pfx = String(this.config.p_prefix || "");
          const start = parseInt(this.config.p_start) || 1;
          const end = parseInt(this.config.p_end) || 20;
          const pad = parseInt(this.config.p_pad) || 0;
          for (let i = start; i <= end; i++) {
            const numStr = pad ? String(i).padStart(pad, "0") : String(i);
            allNums.push(prefixStr + pfx + numStr);
          }
        } else if (this.activePattern === "floor") {
          const startFloor = parseInt(this.config.f_startFloor) || 1;
          const floors = parseInt(this.config.f_floors) || 5;
          const unitsPerFloor = parseInt(this.config.f_units) || 4;
          const pad = parseInt(this.config.f_pad) || 2;
          for (let f = 0; f < floors; f++) {
            const fl = startFloor + f;
            for (let u = 1; u <= unitsPerFloor; u++) {
              const unitPart = String(u).padStart(pad, "0");
              allNums.push(prefixStr + `${fl}${unitPart}`);
            }
          }
        }
      }

      const totalToBeGenerated = this.calculateTotalToBeGenerated();
      this.previewTotal = totalToBeGenerated;
      this.previewChips = allNums;
      this.previewHidden = 0;
      // Keep legacy previewText for any other usages
      this.previewText = allNums.slice(0, 6).join(", ") +
        (totalToBeGenerated > 6 ? " …" : "") +
        ` (Total: ${totalToBeGenerated} units)`;
    },

    openAddModal() {
      this.editingUnit = {
        id: null,
        flat_id: null,
        house_number: "",
        unit_type: this.isCommercialOnly ? "2" : "1",
        occupancy_status: "vacant",
      };
      this.unitModal = true;
    },

    updateShopPreview() {
      if (this.bulkUnitType !== '2') {
        this.shopPreviewText = "—";
        return;
      }
      const start = parseInt(this.shopConfig.start) || 1;
      const end = parseInt(this.shopConfig.end) || 10;
      const step = Math.max(1, parseInt(this.shopConfig.step) || 1);
      const nums = [];
      let count = 0;
      for (let i = start; i <= end; i += step) {
        if (count < 6) { nums.push(i); count++; }
        else break;
      }
      const total = Math.floor((end - start) / step) + 1;
      this.shopPreviewText = nums.join(", ") + (total > 6 ? " …" : "") + ` (Total: ${total} shops)`;
    },

    bulkGenerate() {
      if (this.bulkUnitType === '1' && this.societyType === "multi" && this.selectedBlocks.size === 0) {
        this.showError("Select at least one block");
        return;
      }
      // For shops: only require block selection if shops do NOT act as a single building
      if (this.bulkUnitType === '2' && this.societyType === "multi" && !this.shopActsAsSingleBuilding && this.selectedBlocks.size === 0) {
        this.showError("Select at least one block");
        return;
      }
      this.genLoading = true;
      // When shops act as a single building, send empty selectedBlocks so
      // the backend generates them without any flat_id (society-wide).
      const effectiveBlocks = this.isShopSingleBuildingMode
        ? []
        : [...this.selectedBlocks];
      this.$axios
        .post("societyadmin/units/bulk-generate", {
          societyType: this.societyType,
          selectedBlocks: effectiveBlocks,
          activePattern: this.activePattern,
          config: this.config,
          bulkUnitType: this.bulkUnitType,
        })
        .then((response) => {
          this.genLoading = false;
          if (response.data.status) {
            this.showSuccess(response.data.message);
            this.viewMode = "list";
            this.getUnits();
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(() => {
          this.genLoading = false;
        });
    },

    showSuccess(msg) {
      this.setAlert({
        showAlert: true,
        variant: "green accent-4",
        timeout: "2000",
        message: msg,
      });
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },

  mounted() {
    // Apply from localStorage immediately for fast render
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try { this._applySocietySettings(JSON.parse(userStr)); } catch (e) { /* ignore */ }
    }

    // Fetch fresh society profile to get accurate has_multiple_building / type
    this.$axios.get("societyadmin/societyProfile").then((res) => {
      if (res.data && res.data.status && res.data.society) {
        this._applySocietySettings(res.data.society);
        this.getBlocks(); // re-fetch blocks after confirmed multi status
      }
    }).catch(() => { });
    this.getFlats();
    this.getBlocks();
    this.getUnits();
    this.updateShopPreview();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.icon-btn {
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  background: var(--surface, #ffffff);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: var(--tx2, #374151);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  outline: none;
}

.icon-btn:hover {
  background: var(--surface2, #f8fafc);
  border-color: #cbd5e1;
}

.bulk-btn {
  border-color: rgba(13, 148, 136, 0.2);
}

.bulk-btn:hover {
  background: var(--teal-muted);
}

.unit-cell {
  display: flex;
  align-items: center;
  min-width: 0;
}

.unit-name-text {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
}

.type-badge {
  font-size: 9px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.shop-badge {
  background: var(--badge-shop-bg, #fee2e2);
  color: var(--badge-shop-color, #ef4444);
}

.house-badge {
  background: var(--badge-house-bg, #e0f2fe);
  color: var(--badge-house-color, #0369a1);
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.s-vacant {
  background: var(--status-vacant-bg, #f1f5f9);
  color: var(--status-vacant-color, #475569);
}

.s-occupied {
  background: var(--status-occupied-bg, #dcfce7);
  color: var(--status-occupied-color, #166534);
}

.s-closed {
  background: var(--status-closed-bg, #fee2e2);
  color: var(--status-closed-color, #991b1b);
}

.members-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.member-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.role-lbl {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 3px;
  text-transform: uppercase;
}

.role-lbl.tenant {
  background: var(--role-tenant-bg, #fef3c7);
  color: var(--role-tenant-color, #d97706);
}

.role-lbl.owner {
  background: var(--role-owner-bg, #e0e7ff);
  color: var(--role-owner-color, #4f46e5);
}

.member-val {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #4b5563);
}

.no-members {
  color: var(--tx3, #9ca3af);
  font-style: italic;
}

/* Form Styles */
.form-container,
.form-container-full {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 4px;
}

.form-card {
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.theme--dark .form-card {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--tx2, #374151);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sub-label {
  font-size: 10px;
  color: var(--tx3, #6b7280);
}

.form-input,
.form-select {
  height: 40px;
  padding: 0 12px;
  border: 1.5px solid var(--border, #e2e8f0);
  border-radius: 10px;
  font-size: 13.5px;
  color: var(--tx);
  background: var(--surface, #ffffff);
  outline: none;
  width: 100%;
  transition: all 0.2s ease;
}

.theme--dark .form-input,
.theme--dark .form-select {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
}

.form-input:focus,
.form-select:focus {
  border-color: var(--teal);
  box-shadow: 0 0 0 3px var(--teal-muted);
}

.field-hint {
  font-size: 11px;
  color: var(--tx3, #9ca3af);
  line-height: 1.4;
}

/* Type Selector */
.type-selector {
  display: flex;
  gap: 10px;
}

.type-btn {
  flex: 1;
  height: 42px;
  border: 1.5px solid var(--border, #e2e8f0);
  background: var(--surface, #ffffff);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #4b5563);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.type-btn .v-icon {
  color: inherit !important;
}

.type-btn:hover {
  background: var(--surface2, #f8fafc);
}

.type-btn.active {
  border-color: var(--btn-color, var(--brand, var(--teal)));
  background: var(--btn-color, var(--brand, var(--teal)));
  color: #ffffff;
}

/* Dark mode overrides */
.theme--dark .type-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.75);
}

.theme--dark .type-btn:hover {
  background: rgba(255, 255, 255, 0.09);
}

.theme--dark .type-btn.active {
  background: var(--btn-color, var(--brand, var(--teal)));
  color: #ffffff;
  border-color: var(--btn-color, var(--brand, var(--teal)));
}

/* Pills */
.block-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.block-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: 1.5px solid var(--border, #e2e8f0);
  background: var(--surface, #ffffff);
  cursor: pointer;
  color: var(--tx2, #4b5563);
  transition: all 0.15s ease;
}

.theme--dark .block-pill {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}

.block-pill.selected {
  border-color: var(--teal);
  background: var(--teal-muted);
  color: var(--teal-dark);
}

.theme--dark .block-pill.selected {
  color: var(--teal);
}

.block-pill .pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--tx3, #9ca3af);
}

.block-pill.selected .pill-dot {
  background: var(--teal);
}

/* Tabs */
.pattern-tabs {
  display: flex;
  border: 1.5px solid var(--border, #e2e8f0);
  border-radius: 10px;
  overflow: hidden;
}

.ptab {
  flex: 1;
  padding: 8px 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--tx2, #4b5563);
  cursor: pointer;
  background: var(--surface, #ffffff);
  border: none;
  border-right: 1.5px solid var(--border, #e2e8f0);
  transition: all 0.15s;
  line-height: 1.4;
  outline: none;
}

.theme--dark .ptab {
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.65);
  border-right-color: rgba(255, 255, 255, 0.08);
}

.ptab:last-child {
  border-right: none;
}

.ptab.active {
  background: var(--teal-muted);
  color: var(--teal-dark);
}

.theme--dark .ptab.active {
  color: var(--teal);
}

.ptab-sub {
  font-weight: 400;
  font-size: 10px;
  color: var(--tx3, #9ca3af);
}

.preview-box {
  background: var(--surface2, #f8fafc);
  border: 1.5px dashed var(--border, #e2e8f0);
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  color: var(--tx2, #1e293b);
  font-family: monospace;
  min-height: 44px;
  word-break: break-all;
}

.theme--dark .preview-box {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.8);
}

.select-link-btn {
  font-size: 11px;
  color: var(--teal);
  cursor: pointer;
  font-weight: 700;
}

.select-link-btn:hover {
  text-decoration: underline;
}

.row2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.row3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

/* ── Bulk Page Layout ──────────────────────────────────── */
.bulk-page-content-wrapper {
  padding: 16px 20px;
}

.bulk-layout-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

/* Config column: scrolls normally */
.bulk-config-col {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* Preview column: not sticky (parent has overflow:hidden which breaks sticky anyway) */
.bulk-preview-col {
  /* no positioning — grows naturally with content */
}


/* ── Preview Panel ─────────────────────────────────────── */
.preview-panel {
  background: var(--surface, #ffffff);
  border: 1.5px solid var(--border, #e2e8f0);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.theme--dark .preview-panel {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}

.preview-panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, var(--teal, #0d9488) 0%, var(--teal-dark, #0f766e) 100%);
  color: #fff;
}

.preview-panel-icon {
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.preview-panel-title {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
}

.preview-panel-sub {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 1px;
}

.preview-total-badge {
  margin-left: auto;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
}

/* Chips area — fixed height, scrollable */
.preview-chips-area {
  padding: 14px 14px 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  height: 320px;
  overflow-y: auto;
  align-content: flex-start;
  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: var(--teal, #0d9488) transparent;
}

.preview-chips-area::-webkit-scrollbar {
  width: 5px;
}

.preview-chips-area::-webkit-scrollbar-track {
  background: transparent;
}

.preview-chips-area::-webkit-scrollbar-thumb {
  background: var(--teal, #0d9488);
  border-radius: 10px;
  opacity: 0.6;
}

.preview-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  font-family: monospace;
  letter-spacing: 0.03em;
  border: 1px solid transparent;
  transition: transform 0.1s ease;
}

.preview-chip:hover {
  transform: scale(1.06);
}

.preview-chip--house {
  background: var(--badge-house-bg, #e0f2fe);
  color: var(--badge-house-color, #0369a1);
  border-color: rgba(3, 105, 161, 0.15);
}

.theme--dark .preview-chip--house {
  background: rgba(3, 105, 161, 0.15);
  color: #60b8f5;
  border-color: rgba(3, 105, 161, 0.3);
}

.preview-chip--shop {
  background: var(--badge-shop-bg, #fee2e2);
  color: var(--badge-shop-color, #ef4444);
  border-color: rgba(239, 68, 68, 0.15);
}

.theme--dark .preview-chip--shop {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border-color: rgba(239, 68, 68, 0.3);
}

.preview-chip--more {
  background: var(--surface2, #f1f5f9);
  color: var(--tx3, #64748b);
  border-color: var(--border, #e2e8f0);
  font-family: inherit;
  font-size: 11px;
}

.theme--dark .preview-chip--more {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  border-color: rgba(255, 255, 255, 0.1);
}

.preview-empty {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--tx3, #9ca3af);
  font-size: 12px;
  padding: 24px 0;
}

/* Footer */
.preview-panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid var(--border, #e2e8f0);
  background: var(--surface2, #f8fafc);
}

.theme--dark .preview-panel-footer {
  background: rgba(255, 255, 255, 0.03);
  border-top-color: rgba(255, 255, 255, 0.07);
}

.preview-footer-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--tx3, #64748b);
}

.preview-footer-stat strong {
  color: var(--tx2, #374151);
  font-weight: 700;
}

.theme--dark .preview-footer-stat strong {
  color: rgba(255, 255, 255, 0.85);
}

/* ── Responsive: stack on small/medium screens ──────────── */
@media (max-width: 960px) {
  .bulk-layout-grid {
    grid-template-columns: 1fr;
  }

  .bulk-preview-col {
    position: static;
  }

  .preview-chips-area {
    height: 260px;
  }
}

@media (max-width: 600px) {
  .bulk-page-content-wrapper {
    padding: 10px 12px;
  }

  .preview-panel-header {
    padding: 12px;
  }

  .preview-chips-area {
    padding: 10px 10px 8px;
    gap: 5px;
    height: 200px;
  }

  .preview-chip {
    font-size: 11px;
    padding: 3px 8px;
  }

  .row3 {
    grid-template-columns: 1fr 1fr;
  }
}

.border-top {
  border-top: 1px solid var(--border, #e2e8f0);
}

.gap-3 {
  gap: 12px;
}

.section-head {
  font-size: 11px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Add Unit Button */
.unit-management-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.unit-management-header-actions .btn-premium-primary {
  min-width: 132px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: var(--btn-color, var(--brand, #30384d));
  color: #fff;
  font-size: 13.5px;
  font-weight: 700;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
}

.unit-management-header-actions .btn-premium-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  filter: brightness(1.05);
}

/* ── Shop Generation Card ─────────────────────────────── */
.shop-gen-card {
  border: 1.5px solid var(--badge-shop-color, #ef4444);
  border-radius: 12px;
  background: var(--badge-shop-bg, #fff8f8);
  overflow: hidden;
}

.theme--dark .shop-gen-card {
  background: rgba(239, 68, 68, 0.05);
  border-color: rgba(239, 68, 68, 0.25);
}

/* Header */
.shop-gen-header {
  padding: 14px 18px;
  background: var(--badge-shop-bg, linear-gradient(135deg, #fff1f1 0%, #fff8f8 100%));
  border-bottom: 1px solid var(--badge-shop-color, #fecaca);
}

.theme--dark .shop-gen-header {
  background: rgba(239, 68, 68, 0.08);
  border-bottom-color: rgba(239, 68, 68, 0.2);
}

.shop-gen-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.shop-gen-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(239, 68, 68, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.shop-gen-title {
  font-size: 13px;
  font-weight: 800;
  color: #dc2626;
  letter-spacing: 0.01em;
}

.shop-gen-sub {
  font-size: 11px;
  color: var(--tx3, #6b7280);
  margin-top: 2px;
}

.shop-gen-sub strong {
  color: var(--badge-shop-color, #dc2626);
}

/* Toggle switch */
.shop-toggle-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.shop-toggle-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  min-width: 24px;
  text-align: right;
}

.shop-switch {
  width: 38px;
  height: 20px;
  border-radius: 10px;
  background: #d1d5db;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.shop-switch.on {
  background: var(--badge-shop-color, #ef4444);
}

.shop-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.shop-switch.on .shop-switch-thumb {
  transform: translateX(18px);
}

/* Body */
.shop-gen-body {
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.shop-fields-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.shop-field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.shop-field-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--badge-shop-color, #dc2626);
  padding-left: 2px;
}

/* Preview row */
.shop-preview-row {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--badge-shop-bg, #fff1f1);
  border: 1px dashed var(--badge-shop-color, #fca5a5);
  border-radius: 8px;
  padding: 9px 12px;
}

.shop-preview-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--badge-shop-color, #dc2626);
  margin-right: 4px;
}

.shop-preview-text {
  font-size: 12px;
  color: var(--tx2, #374151);
  font-family: monospace;
}
</style>
